import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { 
  ChevronDown, Building2, Leaf, Landmark, Building, GitMerge,
  ArrowRight, CheckCircle2, Globe2, Shield, Users, Target
} from 'lucide-react';
import SEO from '@/components/SEO';
import WorldMap from '@/components/WorldMap';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const HomePage = () => {
  const { locale } = useParams();

  const services = [
    { 
      title: 'Pharma Engineering & PMC', 
      description: 'End-to-end PMC services including HVAC, utilities, cleanroom design, industrial automation, and audit documentation support.',
      path: '/services/pharma-engineering',
      icon: Building2
    },
    { 
      title: 'Sustainability & ESG / BRSR', 
      description: 'SEBI-mandated BRSR report generation and ESG strategy implementation for India\'s top 1,000 listed companies.',
      path: '/services/sustainability',
      icon: Leaf
    },
    { 
      title: 'Finance & Loans', 
      description: 'Capital structuring, financial audits, and loan facilitation for enterprises expanding capacity or acquiring assets.',
      path: '/services/finance-loans',
      icon: Landmark
    },
    { 
      title: 'Capital Markets', 
      description: 'Real estate investment sales, debt & structured finance, equity advisory, and valuation services across all commercial asset classes.',
      path: '/services/capital-markets',
      icon: Building
    },
    { 
      title: 'Mergers & Acquisitions', 
      description: 'Full-cycle M&A advisory — deal origination, due diligence, valuation, and post-merger integration.',
      path: '/services/mergers-acquisitions',
      icon: GitMerge
    },
  ];

  const stats = [
    { value: 38, suffix: '+', label: 'Years of Experience' },
    { value: 5, suffix: '', label: 'Practice Areas' },
    { value: 500, suffix: '+', label: 'Fortune-Grade Engagements Advised' },
    { value: 2, suffix: '', label: 'Countries of Active Presence' },
  ];

  const differentiators = [
    { icon: Target, title: 'Cross-Vertical Expertise', description: 'Engineering, ESG, finance, and capital markets under one roof' },
    { icon: Shield, title: 'Regulatory Intelligence', description: 'WHO, USFDA, EU cGMP, SEBI BRSR, global M&A compliance' },
    { icon: Users, title: 'C-Suite Advisory', description: 'Trusted by boards for critical strategic decisions' },
    { icon: CheckCircle2, title: 'End-to-End Execution', description: 'Feasibility to commissioning, compliance to capital' },
    { icon: Globe2, title: 'Global Delivery Model', description: 'Local teams, global standards, seamless execution' },
  ];

  const insights = [
    {
      slug: 'gmp-facility-design-considerations',
      title: 'Key Considerations in Designing cGMP-Compliant Pharma Facilities',
      category: 'Pharma Engineering',
      readTime: '6 min read',
      image: '/images/external/photo-1559757148-5c350d0d3c56.jpg'
    },
    {
      slug: 'lessons-25-years-pharma-engineering',
      title: 'Lessons Learned from 25 Years in Pharma Engineering',
      category: 'Pharma Engineering',
      readTime: '8 min read',
      image: '/images/external/photo-1518770660439-4636190af475.jpg'
    },
    {
      slug: 'pharma-hvac-design-mistakes',
      title: 'Common Mistakes in Pharma HVAC Design (And How to Avoid Them)',
      category: 'Pharma Engineering',
      readTime: '7 min read',
      image: '/images/external/photo-1581092580497-e0d23cbdf1dc.jpg'
    },
  ];

  const industries = [
    'Pharmaceuticals', 'Biotechnology', 'Chemicals', 'Capital Markets',
    'Manufacturing', 'Banking & Finance', 'Healthcare', 'Technology'
  ];

  // (Office coordinates handled inside WorldMap component)

  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="pt-20" data-testid="home-page">
      <SEO 
        title="Global Excellence in Strategy & Execution"
        description="H&G partners with the world's leading organizations across engineering, sustainability, capital markets, finance, and transformation. Offices in India and the Netherlands."
        path=""
      />
      {/* Hero Section - Bright/White Background */}
      <section className="min-h-screen flex items-center relative bg-gradient-to-br from-white via-light-grey to-white overflow-hidden" data-testid="hero-section">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal/10 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-teal/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-medium mb-6">
                Strategy. Engineering. Growth.
              </span>
              <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl text-navy leading-tight mb-6">
                At the Intersection of{' '}
                <span className="text-teal">Strategy</span> and{' '}
                <span className="text-teal">Growth</span>
              </h1>
              <p className="text-lg text-mid-grey leading-relaxed mb-8 max-w-xl">
                H&G partners with the world's leading organizations on engineering, sustainability, 
                capital markets, finance, and transformation — across five high-impact practice areas.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to={`/${locale}/services`} className="btn-primary" data-testid="hero-explore-services">
                  Explore Our Services
                </Link>
                <Link to={`/${locale}/about`} className="btn-secondary !text-navy !border-navy hover:!bg-navy/5" data-testid="hero-meet-team">
                  Our Story
                </Link>
                <Link to={`/${locale}/contact`} className="btn-secondary !text-navy !border-navy hover:!bg-navy/5" data-testid="hero-speak-expert">
                  Speak with an Expert
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-teal to-navy rounded-2xl transform rotate-3" />
                <img 
                  src="/images/external/photo-1486406146926-c627a92ad1ab.jpg"
                  alt="Modern corporate architecture"
                  className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-teal/20 rounded-full flex items-center justify-center">
                      <Globe2 className="w-6 h-6 text-teal" />
                    </div>
                    <div>
                      <p className="font-bebas text-3xl text-navy">3</p>
                      <p className="text-sm text-mid-grey">Global Offices</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.button
          onClick={scrollToServices}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-teal scroll-indicator cursor-pointer"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          data-testid="scroll-indicator"
        >
          <ChevronDown className="w-8 h-8" />
        </motion.button>
      </section>

      {/* Marquee Strip */}
      <section className="bg-navy py-4 overflow-hidden" data-testid="marquee-section">
        <div className="marquee-container">
          <div className="marquee-content">
            {[...Array(2)].map((_, i) => (
              <span key={i} className="text-white/80 text-sm tracking-wider mx-8">
                Serving Fortune 500 Organizations · Global Reach · 5 Practice Areas ·
                India | Netherlands · Regulatory-Grade Expertise ·
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-20 md:py-28 lg:py-36 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Our Expertise
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-4xl md:text-5xl text-navy mt-4">
              What We Do
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={service.path}
                  variants={fadeInUp}
                  className={`service-card p-8 rounded-lg ${index >= 3 ? 'lg:col-span-1' : ''}`}
                  data-testid={`service-card-${service.path.split('/').pop()}`}
                >
                  <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="w-7 h-7 text-teal" />
                  </div>
                  <h3 className="font-playfair text-xl text-navy mb-3">{service.title}</h3>
                  <p className="text-mid-grey text-sm leading-relaxed mb-4">{service.description}</p>
                  <Link 
                    to={`/${locale}${service.path}`}
                    className="inline-flex items-center text-teal font-medium text-sm hover:text-navy transition-colors"
                  >
                    Learn More <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 md:py-28 bg-light-grey" data-testid="global-presence-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
              Worldwide Reach
            </motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-4xl md:text-5xl text-navy mt-4">
              A Global Practice. Local Intelligence.
            </motion.h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-white rounded-2xl p-8 shadow-lg"
          >
            {/* World Map (SVG, real geometry, on-theme) */}
          <div className="relative max-w-5xl mx-auto">
            <WorldMap />
          </div>

          {/* Office Grid */}
          <div className="grid grid-cols-2 gap-4 mt-10 max-w-md mx-auto">
            {['India', 'Netherlands'].map((office) => (
              <div key={office} className="text-center py-3 px-4 bg-light-grey rounded-lg">
                <span className="text-sm font-medium text-navy">{office}</span>
              </div>
            ))}
          </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 md:py-28 bg-navy" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="stat-number text-white">
                  <CountUp
                    key={statsInView ? 'animate' : 'static'}
                    start={statsInView ? 0 : stat.value}
                    end={stat.value}
                    duration={statsInView ? 2.5 : 0}
                    suffix={stat.suffix}
                  />
                </div>
                <p className="text-white/80 text-sm mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why H&G */}
      <section className="py-20 md:py-28 lg:py-36 bg-white" data-testid="why-hg-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-teal/10 rounded-2xl transform -rotate-3" />
              <img 
                src="/images/external/photo-1486406146926-c627a92ad1ab.jpg"
                alt="Indian executive in modern office"
                className="relative rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
                Our Advantage
              </motion.span>
              <motion.h2 variants={fadeInUp} className="font-playfair text-4xl md:text-5xl text-navy mt-4 mb-8">
                Why Global Leaders Choose H&G
              </motion.h2>

              <div className="space-y-6">
                {differentiators.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <motion.div 
                      key={item.title}
                      variants={fadeInUp}
                      className="flex items-start"
                    >
                      <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center flex-shrink-0 mr-4">
                        <IconComponent className="w-6 h-6 text-teal" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy mb-1">{item.title}</h4>
                        <p className="text-mid-grey text-sm">{item.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Insights Preview */}
      <section className="py-20 md:py-28 bg-light-grey" data-testid="insights-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12"
          >
            <div>
              <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">
                Thought Leadership
              </motion.span>
              <motion.h2 variants={fadeInUp} className="font-playfair text-4xl md:text-5xl text-navy mt-4">
                Perspectives
              </motion.h2>
            </div>
            <motion.div variants={fadeInUp}>
              <Link 
                to={`/${locale}/insights`}
                className="inline-flex items-center text-teal font-medium hover:text-navy transition-colors mt-4 md:mt-0"
              >
                View All Insights <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {insights.map((insight) => (
              <motion.article
                key={insight.slug}
                variants={fadeInUp}
                className="insight-card bg-white rounded-xl overflow-hidden shadow-sm"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={insight.image} 
                    alt={insight.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-teal bg-teal/10 px-3 py-1 rounded-full">
                      {insight.category}
                    </span>
                    <span className="text-xs text-mid-grey">{insight.readTime}</span>
                  </div>
                  <h3 className="font-playfair text-lg text-navy mb-4 line-clamp-2">
                    {insight.title}
                  </h3>
                  <Link 
                    to={`/${locale}/insights/${insight.slug}`}
                    className="inline-flex items-center text-teal font-medium text-sm hover:text-navy transition-colors"
                  >
                    Read Article <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Industries Strip */}
      <section className="py-16 bg-white border-y border-gray-100" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-center text-navy font-playfair text-2xl mb-8">Industries We Serve</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry) => (
              <span 
                key={industry}
                className="industry-tile px-6 py-3 bg-light-grey rounded-full text-navy text-sm font-medium cursor-pointer"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 md:py-28 bg-teal" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-playfair text-4xl md:text-5xl text-white mb-6">
              Ready to Build Something Exceptional?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Speak with an H&G advisor and unlock a roadmap designed for your next phase of growth.
            </p>
            <Link 
              to={`/${locale}/contact`}
              className="btn-primary inline-block"
              data-testid="cta-schedule-consultation"
            >
              Schedule a Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
